// @ts-nocheck
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, DollarSign, Ticket, ShoppingBag, Trophy, Video } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import VideoVendorSelectionModal from '@/components/video/VideoVendorSelectionModal';
import PackageSelectionModal from '@/components/video/PackageSelectionModal';
import VideoAuthModal from '@/components/video/VideoAuthModal';
import VideoPurchaseModal from '@/components/video/VideoPurchaseModal';
import OrderSuccessModal from '@/components/video/OrderSuccessModal';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { useEventService } from '@/modules/events/hooks/useEventService';
import EventFilters from '@/components/events/EventFilters';
import { parseLocalDate } from '@/utils/formatters';
import { formatTime as fmtTime } from '@/lib/dates';

interface EventTicketOption {
  id: string;
  name: string;
  price: number;
}

interface EventExtra {
  id: string;
  name: string;
  price: number;
  description?: string;
}

interface EventSpectatorTicket {
  id: string;
  name: string;
  price: number;
  is_active: boolean;
  thumbnail_url?: string;
}

interface Event {
  id: string;
  slug: string;
  name: string;
  description?: string;
  start_date: string;
  end_date?: string;
  status: string;
  thumbnail_url?: string;
  gender?: string;
  sport_id?: string;
  sport?: { id: string; name: string }[];
  venue?: {
    name: string;
    city: string;
    state: string;
  };
  event_category_mappings: Array<{
    event_categories: {
      id: string;
      name: string;
    };
  }>;
  event_pricing_tiers: Array<{
    id: string;
    event_id: string;
  }>;
  event_ticket_options: EventTicketOption[];
  event_extras: EventExtra[];
  event_spectator_tickets: EventSpectatorTicket[];
  hasVideoVendors?: boolean;
  isPartnerEvent?: boolean;
}

interface VideoVendor {
  id: string;
  business_name: string;
  description?: string;
  rating_average?: number;
  review_count?: number;
  user_id: string;
}

interface VideoPackage {
  id: string;
  vendor_id: string;
  package_type: 'silver' | 'gold' | 'platinum';
  name: string;
  price_cents: number;
  description: string;
  features: string[];
  is_active: boolean;
}

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const { organization } = useOrganizationContext();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [modalType, setModalType] = useState<'tickets' | 'swag' | null>(null);
  const [eventTournaments, setEventTournaments] = useState<Record<string, any>>({});
  
  // Filter state
  const [dateFilter, setDateFilter] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [sportFilter, setSportFilter] = useState<string>('all');
  const [monthFilter, setMonthFilter] = useState<string>('all');
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  
  // Video purchase related state
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedEventForVideo, setSelectedEventForVideo] = useState<Event | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<VideoVendor | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<VideoPackage | null>(null);
  const [successOrderId, setSuccessOrderId] = useState<string>('');
  const [eventVideoVendors, setEventVideoVendors] = useState<Record<string, boolean>>({});

  const eventService = useEventService(organization.id);

  const fetchEvents = async () => {
    try {
      console.log("🔄 Fetching events with EventService...");
      console.log('🆕 Using EventService for events listing');
      
      // Use EventService with complex select for now
      // EventService automatically filters by organization_id
      let query = supabase
        .from('events')
        .select(`
          id,
          slug,
          name,
          description,
          start_date,
          end_date,
          status,
          thumbnail_url,
          gender,
          sport_id,
          ticket_display_text,
          organization_id,
          organizations!events_organization_id_fkey(
            show_events_on_recruitlook
          ),
          venue:venues!events_main_venue_id_fkey(
            name,
            city,
            state
          ),
          event_category_mappings!event_category_mappings_event_id_fkey(
            event_categories!event_category_mappings_event_category_id_fkey(id, name)
          ),
          event_pricing:event_pricing(pricing_structure),
          sports(
            id,
            name
          )
        `)
        .eq('status', 'published')
        .eq('is_active', true).eq('is_archived', false)
        .eq('organization_id', organization.id); // EventService enforces org filtering
      
      const { data, error } = await query.order('start_date', { ascending: true });

      if (error) {
        console.error("❌ Supabase error:", error);
        throw error;
      }

      console.log("✅ Events fetched successfully:", data?.length || 0);
      
      // Transform the data to match our Event type
        const transformedEvents: Event[] = (data || []).map((event) => {
        const org = event.organizations as any;
        const isPartnerEvent = organization.id === 'bc0245b8-3de8-49f0-a1fe-17a4f67725ce' && 
                               event.organization_id !== 'bc0245b8-3de8-49f0-a1fe-17a4f67725ce' && 
                               org?.show_events_on_recruitlook;
        
        // Extract pricing data from new JSONB structure
        const pricingStructure = Array.isArray(event.event_pricing) && event.event_pricing.length > 0
          ? event.event_pricing[0].pricing_structure
          : {};
        
        return {
          id: event.id,
          slug: event.slug,
          name: event.name,
          description: event.description,
          start_date: event.start_date,
          end_date: event.end_date,
          status: event.status,
          thumbnail_url: event.thumbnail_url,
          gender: event.gender,
          sport_id: event.sport_id,
          sport: event.sports,
          venue: event.venue && typeof event.venue === 'object' && !Array.isArray(event.venue) ? {
            name: (event.venue as any).name,
            city: (event.venue as any).city,
            state: (event.venue as any).state
          } : undefined,
          event_category_mappings: (event.event_category_mappings || []).map(mapping => ({
            event_categories: mapping.event_categories && typeof mapping.event_categories === 'object' ? {
              id: (mapping.event_categories as any).id || '',
              name: (mapping.event_categories as any).name || ''
            } : {
              id: '',
              name: ''
            }
          })),
          // Map from new JSONB structure to expected property names
          event_spectator_tickets: (pricingStructure.spectator_tickets || [])
            .filter((t: any) => t.is_active)
            .map((t: any) => ({
              id: t.id || `ticket-${t.name}`,
              name: t.name,
              price: (t.price || 0) / 100, // Convert cents to dollars for formatPrice
              is_active: t.is_active,
              thumbnail_url: t.thumbnail_url
            })),
          event_extras: (pricingStructure.extras || [])
            .map((e: any) => ({
              id: e.id || `extra-${e.name}`,
              name: e.name,
              price: (e.price || 0) / 100, // Convert cents to dollars for formatPrice
              description: e.description
            })),
          event_pricing_tiers: pricingStructure.registration_tiers || [],
          event_ticket_options: [], // Deprecated - no longer used
          isPartnerEvent
        };
      });
      
      // Filter out past events (where end_date or start_date has passed)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const upcomingEvents = transformedEvents.filter(event => {
        const dateStr = event.end_date || event.start_date;
        const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
        const eventEndDate = new Date(year, month - 1, day);
        return eventEndDate >= today;
      });
      
      setEvents(upcomingEvents);
      
      // Fetch tournament data and video vendor availability for each event
      await Promise.all([
        fetchEventTournaments(upcomingEvents),
        fetchEventVideoVendors(upcomingEvents)
      ]);
    } catch (err: any) {
      console.error("💥 Unexpected error in fetchEvents:", err);
      setError(err.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTournaments = async (events: Event[]) => {
    try {
      const { data: tournaments, error } = await supabase
        .from('tournaments')
        .select('id, event_id, status, tournament_type')
        .in('event_id', events.map(e => e.id))
        .in('status', ['active', 'setup', 'completed']);

      if (error) throw error;

      const tournamentMap: Record<string, any> = {};
      tournaments?.forEach(tournament => {
        tournamentMap[tournament.event_id] = tournament;
      });
      
      setEventTournaments(tournamentMap);
    } catch (error) {
      console.error('Error fetching event tournaments:', error);
    }
  };

  const fetchEventVideoVendors = async (events: Event[]) => {
    try {
      console.log('🔍 Fetching video vendors for events:', events.map(e => e.id));
      
      // Query vendor_event_availability to check which events have video vendors
      const { data: vendorAvailability, error } = await supabase
        .from('vendor_event_availability')
        .select('event_id')
        .in('event_id', events.map(e => e.id));

      if (error) throw error;

      console.log('📊 Vendor availability data:', vendorAvailability);

      const videoVendorMap: Record<string, boolean> = {};
      vendorAvailability?.forEach(availability => {
        videoVendorMap[availability.event_id] = true;
      });
      
      console.log('🗺️ Video vendor map:', videoVendorMap);
      setEventVideoVendors(videoVendorMap);
    } catch (error) {
      console.error('Error fetching event video vendors:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Helper function to construct full image URL from filename
  const getImageUrl = (filename: string | null | undefined) => {
    if (!filename) return null;
    // If it's already a full URL, return as is
    if (filename.startsWith('http')) return filename;
    // Construct the full URL for event-featured bucket (CORRECTED)
    return `https://pvaumvowzncvrdkkvnqq.supabase.co/storage/v1/object/public/event-featured/${filename}`;
  };

  const formatDate = (dateStr: string) => {
    // Parse date parts to avoid timezone issues (YYYY-MM-DD interpreted as UTC)
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to strip HTML tags from description
  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Helper function to truncate description
  const truncateDescription = (description: string, maxLength: number = 120) => {
    const cleanDescription = stripHtml(description);
    if (cleanDescription.length <= maxLength) {
      return cleanDescription;
    }
    return cleanDescription.substring(0, maxLength).trim() + '... read more';
  };

  const formatTime = (timeStr?: string) => fmtTime(timeStr);

  const formatPrice = (priceCents: number) => {
    return `$${priceCents.toFixed(2)}`;
  };

  const openModal = (event: Event, type: 'tickets' | 'swag') => {
    setSelectedEvent(event);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedEvent(null);
    setModalType(null);
  };

  const handleAddToCart = (item: EventTicketOption | EventExtra | EventSpectatorTicket) => {
    // Stub for cart functionality
    console.log("Adding to cart:", item);
    // TODO: Implement actual cart logic
  };

  const handleBuyNow = (ticket: EventSpectatorTicket) => {
    closeModal();
    
    // Navigate to event registration with ticket pre-selected (use slug if available)
    const eventPath = selectedEvent?.slug || selectedEvent?.id;
    navigate(`/events/${eventPath}/register`, {
      state: {
        preselectedItems: {
          spectatorTickets: [{
            id: ticket.id,
            name: ticket.name,
            price: ticket.price,
            quantity: 1
          }]
        },
        skipToStep: 4 // Skip to contact info step
      }
    });
  };

  const handleViewDetails = (eventId: string, eventSlug?: string) => {
    // Use slug if available, otherwise fallback to ID
    navigate(`/events/${eventSlug || eventId}`);
  };


  // Video purchase handlers
  const handlePurchaseVideo = (event: Event) => {
    setSelectedEventForVideo(event);
    setShowVendorModal(true);
  };

  const handleVendorSelected = (vendor: VideoVendor) => {
    setSelectedVendor(vendor);
    setShowVendorModal(false);
    setShowPackageModal(true);
  };

  const handlePackageSelected = (pkg: VideoPackage) => {
    setSelectedPackage(pkg);
    setShowPackageModal(false);
    
    // Check if user is authenticated
    if (!user) {
      setShowAuthModal(true);
    } else {
      setShowPurchaseModal(true);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setShowPurchaseModal(true);
  };

  const handlePurchaseSuccess = (orderId: string) => {
    setShowPurchaseModal(false);
    setSuccessOrderId(orderId);
    setShowSuccessModal(true);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSelectedVendor(null);
    setSelectedPackage(null);
    setSelectedEventForVideo(null);
    setSuccessOrderId('');
  };

  // Helper function to check if event has active spectator tickets
  const hasActiveSpectatorTickets = (event: Event) => {
    return event.event_spectator_tickets && event.event_spectator_tickets.some(ticket => ticket.is_active);
  };

  // Extract available filter options from events
  const availableStates = useMemo(() => {
    const states = new Set<string>();
    events.forEach(event => {
      if (event.venue?.state) {
        states.add(event.venue.state);
      }
    });
    return Array.from(states).sort();
  }, [events]);

  const availableCategories = useMemo(() => {
    const categoriesMap = new Map<string, { id: string; name: string }>();
    events.forEach(event => {
      event.event_category_mappings?.forEach(mapping => {
        if (mapping.event_categories?.id && mapping.event_categories?.name) {
          categoriesMap.set(mapping.event_categories.id, {
            id: mapping.event_categories.id,
            name: mapping.event_categories.name
          });
        }
      });
    });
    return Array.from(categoriesMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  const availableSports = useMemo(() => {
    const sportsMap = new Map<string, { id: string; name: string }>();
    events.forEach(event => {
      if (event.sport && Array.isArray(event.sport)) {
        event.sport.forEach(s => {
          if (s.id && s.name) {
            sportsMap.set(s.id, { id: s.id, name: s.name });
          }
        });
      }
    });
    return Array.from(sportsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [events]);

  const availableMonths = useMemo(() => {
    const monthsSet = new Set<string>();
    events.forEach(event => {
      const date = parseLocalDate(event.start_date);
      const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      monthsSet.add(monthYear);
    });
    return Array.from(monthsSet);
  }, [events]);

  // Apply filters
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      // Date filter
      if (dateFilter) {
        const eventDate = event.start_date.split('T')[0];
        const endDate = event.end_date?.split('T')[0] || eventDate;
        if (dateFilter < eventDate || dateFilter > endDate) {
          return false;
        }
      }

      // State filter
      if (stateFilter && stateFilter !== 'all') {
        if (event.venue?.state !== stateFilter) {
          return false;
        }
      }

      // Gender filter
      if (genderFilter && genderFilter !== 'all') {
        if (event.gender?.toLowerCase() !== genderFilter.toLowerCase()) {
          return false;
        }
      }

      // Sport filter
      if (sportFilter && sportFilter !== 'all') {
        const hasSport = event.sport?.some(s => s.id === sportFilter);
        if (!hasSport) {
          return false;
        }
      }

      // Month filter
      if (monthFilter && monthFilter !== 'all') {
        const date = parseLocalDate(event.start_date);
        const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        if (monthYear !== monthFilter) {
          return false;
        }
      }

      // Category filter
      if (categoryFilters.length > 0) {
        const eventCategoryIds = event.event_category_mappings?.map(m => m.event_categories?.id) || [];
        const hasCategory = categoryFilters.some(catId => eventCategoryIds.includes(catId));
        if (!hasCategory) {
          return false;
        }
      }

      return true;
    });
  }, [events, dateFilter, stateFilter, genderFilter, sportFilter, monthFilter, categoryFilters]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-64">
          <div className="text-lg">Loading events...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">
          <h2 className="text-xl font-semibold mb-2">Error Loading Events</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Upcoming Events</h1>
        <p className="text-muted-foreground">Discover and register for upcoming athletic events</p>
      </div>

      {/* Event Filters */}
      <EventFilters
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        stateFilter={stateFilter}
        setStateFilter={setStateFilter}
        genderFilter={genderFilter}
        setGenderFilter={setGenderFilter}
        sportFilter={sportFilter}
        setSportFilter={setSportFilter}
        monthFilter={monthFilter}
        setMonthFilter={setMonthFilter}
        categoryFilters={categoryFilters}
        setCategoryFilters={setCategoryFilters}
        availableStates={availableStates}
        availableSports={availableSports}
        availableMonths={availableMonths}
        availableCategories={availableCategories}
      />

      {filteredEvents.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-xl font-semibold text-muted-foreground mb-2">
            {events.length === 0 ? 'No Events Available' : 'No Events Match Your Filters'}
          </h3>
          <p className="text-muted-foreground">
            {events.length === 0 ? 'Check back soon for upcoming events!' : 'Try adjusting your filters to see more events.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event, eventIndex) => {
            console.log(`🎨 Rendering event ${eventIndex + 1}:`, event.name);
            
            const imageUrl = getImageUrl(event.thumbnail_url);
            console.log("🖼️ Featured image URL check:", {
              thumbnail_url: event.thumbnail_url,
              constructed_url: imageUrl,
              has_image: !!imageUrl
            });
            
            console.log("🏷️ Categories check:", {
              mappings: event.event_category_mappings,
              count: event.event_category_mappings?.length || 0,
              has_categories: event.event_category_mappings?.length > 0
            });
            console.log("🎫 Tickets check:", {
              tickets: event.event_ticket_options,
              count: event.event_ticket_options?.length || 0,
              has_tickets: event.event_ticket_options?.length > 0
            });
            console.log("🎟️ Spectator tickets check:", {
              spectator_tickets: event.event_spectator_tickets,
              count: event.event_spectator_tickets?.length || 0,
              has_active_spectator_tickets: hasActiveSpectatorTickets(event)
            });
            console.log("🛒 Extras check:", {
              extras: event.event_extras,
              count: event.event_extras?.length || 0,
              has_extras: event.event_extras?.length > 0
            });

            return (
              <Card key={event.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                {/* Featured Image Section */}
                {imageUrl ? (
                  <div className="h-64 overflow-hidden">
                    <img 
                      src={imageUrl}
                      alt={event.name}
                      className="w-full h-full object-cover"
                      onLoad={() => {
                        console.log("✅ Featured image loaded successfully for:", event.name);
                      }}
                      onError={(e) => {
                        console.error("❌ Featured image failed to load for event:", event.name);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-64 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500">No Image Available</span>
                  </div>
                )}
                
                <CardHeader className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <CardTitle className="text-xl font-semibold">{event.name}</CardTitle>
                    {event.isPartnerEvent && (
                      <Badge variant="outline" className="bg-secondary/10 text-secondary border-secondary">
                        Partner Event
                      </Badge>
                    )}
                  </div>
                  
                  {/* Event Categories */}
                  {event.event_category_mappings && event.event_category_mappings.length > 0 ? (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2 justify-center">
                        {event.event_category_mappings.map((mapping, idx) => {
                          console.log(`🏷️ Rendering category ${idx}:`, mapping);
                          return (
                            <Badge 
                              key={mapping.event_categories.id || idx} 
                              variant="secondary"
                              className="text-xs"
                            >
                              {mapping.event_categories.name || 'Unnamed Category'}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <span className="text-yellow-500 text-xs">No categories assigned</span>
                    </div>
                  )}
                </CardHeader>
                
                <CardContent className="space-y-3 text-center p-4">
                  {/* Date and Venue Section */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-center text-sm">
                      <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="font-bold text-blue-900">
                        {formatDate(event.start_date)}
                        {event.end_date && event.end_date !== event.start_date && (
                          <span> - {formatDate(event.end_date)}</span>
                        )}
                      </span>
                    </div>
                    
                    {event.venue && (
                      <div className="flex items-center justify-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        {event.venue.name}, {event.venue.city}, {event.venue.state}
                      </div>
                    )}
                  </div>


                  <div className="flex flex-col gap-2 pt-4">
                    <Button 
                      className="w-full"
                      onClick={() => handleViewDetails(event.id, event.slug)}
                    >
                      View Details
                    </Button>
                    
                     
                     {/* Purchase Video Button - Only show if video vendors are available */}
                     {(() => {
                       const hasVideoVendors = eventVideoVendors[event.id];
                       console.log(`🎥 Video vendor check for event ${event.name}:`, {
                         eventId: event.id,
                         hasVideoVendors,
                         eventVideoVendors,
                         showButton: hasVideoVendors
                       });
                       return hasVideoVendors;
                     })() && (
                       <Button 
                         variant="outline" 
                         className="w-full flex items-center gap-2"
                         onClick={() => handlePurchaseVideo(event)}
                       >
                         <Video className="w-4 h-4" />
                         Purchase Video
                       </Button>
                     )}
                    
                    {/* View Bracket Button - Only show for events with tournaments */}
                    {eventTournaments[event.id] && (
                      <Button 
                        variant="outline" 
                        className="w-full flex items-center gap-2"
                        asChild
                      >
                        <Link to={`/tournaments/${eventTournaments[event.id].id}/bracket`}>
                          <Trophy className="w-4 h-4" />
                          View Bracket
                        </Link>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal for Spectator Tickets */}
      <Dialog open={modalType === 'tickets'} onOpenChange={closeModal}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Spectator Tickets</DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <h3 className="font-semibold">{selectedEvent.name}</h3>
              
              {selectedEvent.event_spectator_tickets.filter(ticket => ticket.is_active).map((ticket) => (
                <div key={ticket.id} className="flex items-center gap-4 p-4 border rounded-lg">
                  {ticket.thumbnail_url && (
                    <img 
                      src={ticket.thumbnail_url} 
                      alt={ticket.name} 
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0" 
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium">{ticket.name}</h4>
                    <p className="text-lg font-semibold text-green-600">
                      {formatPrice(ticket.price)}
                    </p>
                  </div>
                  <Button 
                    className="ml-auto"
                    onClick={() => handleBuyNow(ticket)}
                  >
                    Buy Now
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal for Event Swag */}
      <Dialog open={modalType === 'swag'} onOpenChange={closeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Event Swag</DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <h3 className="font-semibold">{selectedEvent.name}</h3>
              
              {selectedEvent.event_extras.map((extra) => (
                <div key={extra.id} className="border rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium">{extra.name}</h4>
                      {extra.description && (
                        <p className="text-sm text-gray-600 mt-1">{extra.description}</p>
                      )}
                      <p className="text-lg font-semibold text-green-600">
                        {formatPrice(extra.price)}
                      </p>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full"
                    onClick={() => handleAddToCart(extra)}
                  >
                    Add to Cart
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Video Vendor Selection Modal */}
      <VideoVendorSelectionModal
        open={showVendorModal}
        onOpenChange={setShowVendorModal}
        eventId={selectedEventForVideo?.id || ''}
        eventName={selectedEventForVideo?.name || ''}
        onVendorSelected={handleVendorSelected}
      />

      {/* Package Selection Modal */}
      <PackageSelectionModal
        open={showPackageModal}
        onOpenChange={setShowPackageModal}
        vendor={selectedVendor}
        onPackageSelected={handlePackageSelected}
      />

      {/* Auth Modal */}
      <VideoAuthModal
        open={showAuthModal}
        onOpenChange={setShowAuthModal}
        onAuthSuccess={handleAuthSuccess}
      />

      {/* Purchase Modal */}
      <VideoPurchaseModal
        open={showPurchaseModal}
        onOpenChange={setShowPurchaseModal}
        vendor={selectedVendor}
        selectedPackage={selectedPackage}
        event={selectedEventForVideo}
        onSuccess={handlePurchaseSuccess}
      />

      {/* Success Modal */}
      <OrderSuccessModal
        open={showSuccessModal}
        onOpenChange={handleCloseSuccessModal}
        orderId={successOrderId}
        packageName={selectedPackage?.package_type || ''}
        vendorName={selectedVendor?.business_name || ''}
        eventName={selectedEventForVideo?.name || ''}
        totalAmount={selectedPackage?.price_cents || 0}
      />
    </div>
  );
};

export default EventsPage;
